import { createFileRoute, Link } from "@tanstack/react-router";
import useTranslation from "../../../hooks/useTranslation";
import { THINKERA_LEVEL } from "../../../constants";
import { useGeneratedTextCreateSpeech } from "../../../hooks/useGeneratedTextUpdateSpeech";
import { useGeneratedTextDetails } from "../../../hooks/useGeneratedTextDetails";
import { useDailyTextStartGeneration } from "../../../hooks/useDailyTextStartGeneration";
import { useIncrementGeneratedTextViewCounter } from "../../../hooks/useIncrementGeneratedTextViewCounter";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {
  usePaginatedVocabularyList,
  VocabularyDto,
} from "../../../hooks/usePaginatedVocabularyList";
import { useGeneratedTextInlineContinuation } from "../../../hooks/useGeneratedTextInlineContinuation";
import { resetStreamingTextIntegrator } from "../../../hooks/useStreamingTextIntegrator";
import { GeneratedTextDetailsTextPane } from "../../../components/GeneratedTextDetailsTextPane";
import { CreateVocabularyTextInlinePreview } from "../../../components/CreateVocabularyTextInlinePreview";
import { LanguageHelperPanel } from "../../../components/LanguageHelperPanel";
import { VocabularyPreviewCard } from "../../../components/VocabularyPreviewCard";
import { useBarkAccountQuery } from "../../../hooks/useBarkAccountQuery";
import { useVocabularyUpdate } from "../../../hooks/useVocabularyUpdate";
import { useUserSettings } from "../../../hooks/useUserSettings";
import { SubmitHandler, useForm } from "react-hook-form";
import { useChapterRetrieveQuery } from "../../../hooks/useChapterRetrieveQuery";
import { useLanguageSpace } from "../../../hooks/useLanguageSpace";
import {
  getVocabularyBaseWord,
  getVocabularyPrimaryWord,
  getVocabularyPrimaryTranslation,
  getVocabularyStructureDetails,
  getVocabularySurfaceWord,
  normalizeVocabularyTranslationDetails,
} from "../../../utils/vocabularyTranslationDetails";
import { highlightVocabularyWordInSentence } from "../../../utils/highlightVocabularyWordInSentence";
import { VocabularyPronunciationButton } from "../../../components/VocabularyPronunciationButton";
import { VocabularyRevealText } from "../../../components/VocabularyRevealText";
import { resolveGeneratedTextContent } from "../../../utils/generatedTextContent";
import { getChapterContinueButtonLabelKey } from "../../../utils/getChapterContinueButtonLabelKey";
import { resolveActiveTextHighlightWords } from "../../../utils/resolveActiveTextHighlightWords";
import { resolveSelectedVocabularyWordsForChapterGeneration } from "../../../utils/resolveSelectedVocabularyWordsForChapterGeneration";
import { resolveWordAddedToastKey } from "../../../utils/resolveWordAddedToastKey";
import { shouldRefetchVocabularyAfterCompletion } from "../../../utils/shouldRefetchVocabularyAfterCompletion";
import {
  resolveNextPlaybackRate,
  resolveSeekTime,
} from "../../../utils/readingAudioPlayback";
import {
  pruneVocabularySelectionToExistingIds,
  resolveDefaultSelectedVocabularyIdsForGeneratedReinforcementText,
} from "../../../utils/vocabularySelectionState";
import { resetAppScrollPosition } from "../../../utils/resetAppScrollPosition";
import {
  advanceVocabularyRevealStage,
  VOCABULARY_REVEAL_STAGE_COUNT,
} from "../../../utils/vocabularyRevealEffect";
import { notifySnackbar } from "../../../lib/notifySnackbar";
import { getCsrfCookie } from "../../../lib/utils";
import {
  LightBulbIcon,
  PauseIcon,
  PlayIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import type { ImageImportMetadata } from "../../../hooks/types/GeneratedTexts";

export const Route = createFileRoute("/app/reeprise/$generatedTextId")({
  component: GeneratedTextDetailsPage,
  validateSearch: (search) => {
    const parsedFocusToken = Number(search.focusToken);
    return {
      chapter:
        search.chapter === undefined ? undefined : Number(search.chapter),
      focusWord:
        typeof search.focusWord === "string" ? search.focusWord : undefined,
      focusSentence:
        typeof search.focusSentence === "string"
          ? search.focusSentence
          : undefined,
      focusToken: Number.isFinite(parsedFocusToken)
        ? parsedFocusToken
        : undefined,
    };
  },
});

function resolveImageImportStageLabelKey(
  processingStage: ImageImportMetadata["processing_stage"],
  mode: ImageImportMetadata["mode"],
) {
  if (processingStage === "FETCHING_URL") {
    return "article_import_loading_reading";
  }
  if (processingStage === "EXTRACTING_ARTICLE") {
    return "article_import_loading_reading";
  }
  if (processingStage === "READING_IMAGE") {
    return "image_import_processing_reading_image";
  }
  if (processingStage === "EXTRACTING_TEXT") {
    return "image_import_processing_extracting_text";
  }
  if (processingStage === "CREATING_CARD") {
    return "image_import_processing_creating_card";
  }
  if (processingStage === "ADAPTING_TEXT") {
    return mode === "adapt"
      ? "image_import_processing_adapting_text"
      : "image_import_processing_creating_card";
  }
  return "image_import_processing_reading_image";
}

function resolveImageImportFailureMessage(
  metadata: ImageImportMetadata | null | undefined,
  fallbackMessage: string,
) {
  if (metadata?.source === "url") {
    if (fallbackMessage.trim()) {
      return fallbackMessage.trim();
    }
    return "Something went wrong while adapting the article.";
  }
  const errorCode = metadata?.processing_error_code ?? "";
  if (errorCode === "UNSUPPORTED_SOURCE") {
    return "This image doesn't contain a continuous reading text. Select extract vocabulary after the image upload.";
  }
  if (errorCode === "ARTICLE_SELECTION_INCOHERENT") {
    return "We couldn't reliably determine one article from the selected area.";
  }
  if (errorCode === "LOW_READING_ORDER_CONFIDENCE") {
    return "We couldn't reliably determine the main text from this image.";
  }
  if (errorCode === "LOW_OCR_CONFIDENCE") {
    return "This image is too difficult to read accurately. Try a clearer photo.";
  }
  if (errorCode === "EMPTY_TEXT") {
    return "We could not read enough text from this image. Please try again with a clearer photo.";
  }
  return fallbackMessage.trim() || "We couldn't create a reading card from this image.";
}

function GeneratedTextDetailsPage() {
  const { translate } = useTranslation();
  const { generatedTextId } = Route.useParams();
  const { chapter, focusWord, focusSentence, focusToken } = Route.useSearch();
  const { createSpeechMutation } = useGeneratedTextCreateSpeech();
  const reepriseDetails = useGeneratedTextDetails(Number(generatedTextId));
  const startDailyTextGeneration = useDailyTextStartGeneration();
  const { barkAccountQuery } = useBarkAccountQuery();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const userSettings = useUserSettings({ enabled: true });
  const chapterDetails = useChapterRetrieveQuery({
    chapterCounter: chapter,
    generatedTextId: Number(generatedTextId),
  });
  const incrementCounter = useIncrementGeneratedTextViewCounter({
    textId: Number(generatedTextId),
  });

  const topRef = useRef<HTMLDivElement | null>(null);
  const startGenerationRequestKeyRef = useRef<string | null>(null);

  const [selectedVocabularyIds, setSelectedVocabularyIds] = useState<number[]>(
    [],
  );
  const [deselectedVocabularyIds, setDeselectedVocabularyIds] = useState<
    number[]
  >([]);
  const [recentVocabulary, setRecentVocabulary] = useState<
    { word: string; translation?: string | null }[]
  >([]);
  const [optimisticSelectedWordsById, setOptimisticSelectedWordsById] = useState<
    Record<number, string>
  >({});
  const [titleOnlyVocabularyIds, setTitleOnlyVocabularyIds] = useState<number[]>([]);
  const [focusRequest, setFocusRequest] = useState<{
    word: string;
    sentence?: string | null;
    token: number;
  } | null>(null);
  const [grammarLesson, setGrammarLesson] = useState<{
    title: string;
    summary: string;
    points: { pattern: string; example: string; explanation: string }[];
  } | null>(null);
  const [isGrammarLessonLoading, setIsGrammarLessonLoading] = useState(false);
  const [grammarLessonError, setGrammarLessonError] = useState<string | null>(null);
  const [showTextScopedTrainer, setShowTextScopedTrainer] = useState(false);
  const [isLanguageHelperOpen, setIsLanguageHelperOpen] = useState(false);
  const selectedVocabularyIdsRef = useRef<number[]>([]);
  const deselectedVocabularyIdsRef = useRef<number[]>([]);
  const audioSrc =
    chapter !== undefined
      ? `/streams/audio/${generatedTextId}/?chapter=${chapter}`
      : `/streams/audio/${generatedTextId}/`;

  const onVocabularyCreate = (
    vocabularyDto: VocabularyDto,
    options?: { source: "title" | "text" },
  ) => {
    notifySnackbar({
      message: translate(resolveWordAddedToastKey(vocabularyDto.word)),
    });
    setSelectedVocabularyIds((oldValue) =>
      oldValue.includes(vocabularyDto.id)
        ? oldValue
        : [...oldValue, vocabularyDto.id],
    );
    setDeselectedVocabularyIds((prev) =>
      prev.filter((entry) => entry !== vocabularyDto.id),
    );
    if (options?.source === "title") {
      setTitleOnlyVocabularyIds((prev) =>
        prev.includes(vocabularyDto.id) ? prev : [...prev, vocabularyDto.id],
      );
    }
    if (vocabularyDto.id && vocabularyDto.word?.trim()) {
      setOptimisticSelectedWordsById((prev) => ({
        ...prev,
        [vocabularyDto.id]: vocabularyDto.word.trim(),
      }));
    }
    if (vocabularyDto.word?.trim()) {
      setRecentVocabulary((prev) => [
        { word: vocabularyDto.word, translation: vocabularyDto.translation },
        ...prev.filter((item) => item.word !== vocabularyDto.word),
      ].slice(0, 5));
    }
  };

  useEffect(() => {
    resetAppScrollPosition();
    setSelectedVocabularyIds([]);
    setDeselectedVocabularyIds([]);
    setOptimisticSelectedWordsById({});
    setTitleOnlyVocabularyIds([]);
    setShowTextScopedTrainer(false);
    setIsLanguageHelperOpen(false);
  }, [generatedTextId, chapter]);

  useEffect(() => {
    selectedVocabularyIdsRef.current = selectedVocabularyIds;
    deselectedVocabularyIdsRef.current = deselectedVocabularyIds;
  }, [deselectedVocabularyIds, selectedVocabularyIds]);

  useEffect(() => {
    const openLanguageHelperOverlay = () => {
      setIsLanguageHelperOpen(true);
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent("focus-language-helper"));
      });
    };
    window.addEventListener(
      "open-language-helper-overlay",
      openLanguageHelperOverlay,
    );
    return () => {
      window.removeEventListener(
        "open-language-helper-overlay",
        openLanguageHelperOverlay,
      );
    };
  }, []);

  useEffect(() => {
    if (!focusWord && !focusSentence) {
      return;
    }
    setFocusRequest({
      word: focusWord ?? "",
      sentence: focusSentence ?? null,
      token: focusToken ?? Date.now(),
    });
  }, [focusSentence, focusToken, focusWord]);

  useEffect(() => {
    const text = reepriseDetails.data;
    if (
      !text ||
      text.status !== "PENDING" ||
      chapter !== undefined
    ) {
      return;
    }
    const isDeferredImageReadingText =
      text.origin === "MANUAL" && text.image_import_metadata?.source === "image";
    const isDeferredArticleUrlText =
      text.origin === "MANUAL" && text.image_import_metadata?.source === "url";
    const isDeferredDailyText = text.origin === "DAILY_NUDGE";
    if (!isDeferredDailyText && !isDeferredImageReadingText && !isDeferredArticleUrlText) {
      return;
    }
    const requestKey = `${text.id}:${chapter ?? "root"}`;
    if (startGenerationRequestKeyRef.current === requestKey) {
      return;
    }
    startGenerationRequestKeyRef.current = requestKey;
    void startDailyTextGeneration.mutateAsync({
      generatedTextId: Number(generatedTextId),
    });
  }, [
    chapter,
    generatedTextId,
    reepriseDetails.data,
    startDailyTextGeneration,
  ]);

  const inlineContinuationMutation = useGeneratedTextInlineContinuation({
    generatedTextId: Number(generatedTextId),
  });

  const paginatedVocabularyList = usePaginatedVocabularyList({
    generated_text_id: Number(generatedTextId),
  });
  const completionVocabularyRefetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const id = setTimeout(() => {
      void incrementCounter.mutateAsync();
    }, 5_000);

    return () => clearTimeout(id);
  }, [generatedTextId, incrementCounter]);

  useEffect(() => {
    const completionRefetchKey = `${generatedTextId}:${chapter ?? "root"}`;
    if (
      !shouldRefetchVocabularyAfterCompletion({
        status: reepriseDetails.data?.status,
        completionRefetchKey,
        lastRefetchedCompletionKey: completionVocabularyRefetchKeyRef.current,
      })
    ) {
      return;
    }
    completionVocabularyRefetchKeyRef.current = completionRefetchKey;
    void paginatedVocabularyList.refetch();
  }, [
    chapter,
    generatedTextId,
    paginatedVocabularyList,
    reepriseDetails.data?.status,
  ]);

  const vocabularies =
    paginatedVocabularyList.data?.pages.flatMap((page) => page.results) || [];
  const resolvedDetailsTitle = resolveGeneratedTextContent({
    rawText:
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "",
    fallbackTitle: reepriseDetails.data?.topic ?? "",
    generatingTitleCopy: translate("text_card_generating_title"),
    isComplete: reepriseDetails.data?.status === "COMPLETE",
    maxWords: reepriseDetails.data?.word_number,
    enforceTitleFirst: true,
  }).displayTitle.trim();
  const isPodcastText = reepriseDetails.data?.text_type === "PODCAST";
  const podcastTranscript = useMemo(() => {
    if (chapter !== undefined) {
      if (
        chapterDetails.data &&
        chapterDetails.data.status === "COMPLETE" &&
        "content" in chapterDetails.data
      ) {
        return chapterDetails.data.content.trim();
      }
      return "";
    }
    const rawText =
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "";
    return resolveGeneratedTextContent({
      rawText,
      fallbackTitle: reepriseDetails.data?.topic ?? "",
      generatingTitleCopy: translate("text_card_generating_title"),
      isComplete: reepriseDetails.data?.status === "COMPLETE",
      maxWords: reepriseDetails.data?.word_number,
      enforceTitleFirst: true,
    }).displayStory.trim();
  }, [chapter, chapterDetails.data, reepriseDetails.data, translate]);
  const resolvedDisplayText = useMemo(() => {
    if (chapter !== undefined) {
      if (
        chapterDetails.data &&
        chapterDetails.data.status === "COMPLETE" &&
        "content" in chapterDetails.data
      ) {
        return chapterDetails.data.content.trim();
      }
      return "";
    }
    const rawText =
      reepriseDetails.data && "chatgpt_output" in reepriseDetails.data
        ? reepriseDetails.data.chatgpt_output ?? ""
        : "";
    return resolveGeneratedTextContent({
      rawText,
      fallbackTitle: reepriseDetails.data?.topic ?? "",
      generatingTitleCopy: translate("text_card_generating_title"),
      isComplete: reepriseDetails.data?.status === "COMPLETE",
      maxWords: reepriseDetails.data?.word_number,
      enforceTitleFirst: true,
    }).displayStory.trim();
  }, [chapter, chapterDetails.data, reepriseDetails.data, translate]);
  const persistedTitleOnlyVocabularyIds = useMemo(
    () =>
      vocabularies
        .filter((voc) => voc.sentence?.trim() === resolvedDetailsTitle)
        .map((voc) => voc.id),
    [resolvedDetailsTitle, vocabularies],
  );
  const selectedVocabularyWords = useMemo(
    () =>
      vocabularies
        .filter(
          (voc) =>
            selectedVocabularyIds.includes(voc.id) &&
            !titleOnlyVocabularyIds.includes(voc.id) &&
            !persistedTitleOnlyVocabularyIds.includes(voc.id),
        )
        .map((voc) => voc.word),
    [
      vocabularies,
      selectedVocabularyIds,
      titleOnlyVocabularyIds,
      persistedTitleOnlyVocabularyIds,
    ],
  );
  const titleVocabularyWords = useMemo(() => {
    const persistedTitleVocabularyWords = vocabularies
      .filter((voc) => persistedTitleOnlyVocabularyIds.includes(voc.id))
      .map((voc) => voc.word);
    const localTitleVocabularyWords = titleOnlyVocabularyIds
      .filter((id) => !persistedTitleOnlyVocabularyIds.includes(id))
      .map((id) => optimisticSelectedWordsById[id] ?? "");
    return [
      ...persistedTitleVocabularyWords,
      ...localTitleVocabularyWords,
    ].filter((word): word is string => Boolean(word?.trim()));
  }, [
    optimisticSelectedWordsById,
    persistedTitleOnlyVocabularyIds,
    titleOnlyVocabularyIds,
    vocabularies,
  ]);
  const selectedVocabularyWordsForGeneration = useMemo(
    () => {
      return resolveSelectedVocabularyWordsForChapterGeneration({
        optimisticWordsById: optimisticSelectedWordsById,
        selectedVocabularyIds,
        vocabularies,
        displayText: resolvedDisplayText,
        generatedTextId: Number(generatedTextId),
        chapterCounter: chapter,
      });
    },
    [
      chapter,
      generatedTextId,
      optimisticSelectedWordsById,
      resolvedDisplayText,
      selectedVocabularyIds,
      vocabularies,
    ],
  );
  const chapterContinueButtonLabelKey = getChapterContinueButtonLabelKey(
    selectedVocabularyWordsForGeneration.length,
  );
  const importMetadata = reepriseDetails.data?.image_import_metadata ?? null;
  const imageImportMetadata =
    importMetadata?.source === "image" ? importMetadata : null;
  const articleImportMetadata =
    importMetadata?.source === "url" ? importMetadata : null;
  const isImportedReadingText =
    imageImportMetadata !== null || articleImportMetadata !== null;
  const isInlineContinuationExperimentEnabled =
    userSettings.data?.show_experimental_features === true &&
    chapter === undefined &&
    reepriseDetails.data?.status === "COMPLETE" &&
    !isImportedReadingText;
  const showInlineContinuationPreview = isInlineContinuationExperimentEnabled;
  const inlineContinuationErrorMessage = inlineContinuationMutation.isError
    ? translate("generated_text_inline_continue_error")
    : null;
  const activeHighlightWords = resolveActiveTextHighlightWords({
    selectedVocabularyWords,
    titleVocabularyWords,
    chapterGeneratedWords:
      chapter !== undefined &&
      chapterDetails.data &&
      chapterDetails.data.status !== "NON_EXISTENT"
        ? chapterDetails.data.generated_words
        : undefined,
  });
  const supportEmail =
    barkAccountQuery.data?.support_email?.trim() || "office@uxhulu.com";
  const failedContactHref = `mailto:${supportEmail}?subject=${encodeURIComponent(
    "Generated text incident",
  )}&body=${encodeURIComponent(
    `Hi,\n\nI could not open generated text #${generatedTextId} because the text generation failed.\n\nPlease let me know when it is fixed.\n`,
  )}`;
  const imageImportStatusCard =
    imageImportMetadata && chapter === undefined ? (
      <ImageImportStatusCard
        metadata={imageImportMetadata}
        status={reepriseDetails.data?.status}
      />
    ) : null;
  const sourceMetadataCard =
    articleImportMetadata && chapter === undefined ? (
      <ArticleImportSourceCard metadata={articleImportMetadata} />
    ) : null;
  const hasSameIds = (left: number[], right: number[]) =>
    left.length === right.length &&
    left.every((value, index) => value === right[index]);

  useEffect(() => {
    const availableVocabularyIds = vocabularies.map((entry) => entry.id);
    const currentSelectedIds = selectedVocabularyIdsRef.current;
    const currentDeselectedIds = deselectedVocabularyIdsRef.current;
    const preselectedIds =
      resolveDefaultSelectedVocabularyIdsForGeneratedReinforcementText({
        origin: reepriseDetails.data?.origin,
        struggleWordIds: [],
        availableIds: availableVocabularyIds,
        selectedIds: currentSelectedIds,
        deselectedIds: currentDeselectedIds,
      });
    const prunedSelection = pruneVocabularySelectionToExistingIds({
      selectedIds: preselectedIds,
      deselectedIds: currentDeselectedIds,
      availableIds: availableVocabularyIds,
    });
    setSelectedVocabularyIds((prev) => {
      const next = prunedSelection.selectedIds;
      return hasSameIds(next, prev) ? prev : next;
    });
    setDeselectedVocabularyIds((prev) => {
      const next = prunedSelection.deselectedIds;
      return hasSameIds(next, prev) ? prev : next;
    });
  }, [
    reepriseDetails.data?.origin,
    vocabularies,
  ]);

  const continueTextInlineFromSelection = async () => {
    const words = selectedVocabularyWordsForGeneration;
    resetStreamingTextIntegrator({ textId: Number(generatedTextId) });
    await inlineContinuationMutation.mutateAsync({ words });
  };

  const toggleTrainerForCurrentText = () => {
    setShowTextScopedTrainer((prev) => !prev);
  };

  const generateGrammarLesson = async () => {
    setIsGrammarLessonLoading(true);
    setGrammarLessonError(null);
    try {
      const res = await fetch(
        `/api/reeprise/generated_texts/${generatedTextId}/grammar_lesson/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFTOKEN": getCsrfCookie(),
          },
          body: JSON.stringify({}),
        },
      );
      if (!res.ok) {
        throw new Error("grammar_lesson_request_failed");
      }
      const payload = (await res.json()) as {
        title?: string;
        summary?: string;
        points?: { pattern?: string; example?: string; explanation?: string }[];
      };
      setGrammarLesson({
        title: payload.title?.trim() || "",
        summary: payload.summary?.trim() || "",
        points: Array.isArray(payload.points)
          ? payload.points
              .map((point) => ({
                pattern: point.pattern?.trim() || "",
                example: point.example?.trim() || "",
                explanation: point.explanation?.trim() || "",
              }))
              .filter(
                (point) =>
                  point.pattern.length > 0 ||
                  point.example.length > 0 ||
                  point.explanation.length > 0,
              )
          : [],
      });
    } catch {
      setGrammarLessonError(translate("generated_text_grammar_lesson_error"));
      notifySnackbar({
        message: translate("generated_text_grammar_lesson_error"),
      });
    } finally {
      setIsGrammarLessonLoading(false);
    }
  };

  if (reepriseDetails.data?.status === "FAILED") {
    const customFailureBody = imageImportMetadata
      ? resolveImageImportFailureMessage(
          imageImportMetadata,
          "chatgpt_output" in reepriseDetails.data
            ? reepriseDetails.data.chatgpt_output ?? ""
            : "",
        )
      : undefined;
    return (
      <FailedGeneratedTextScreen
        contactHref={failedContactHref}
        customBody={customFailureBody}
        generatedTextId={Number(generatedTextId)}
        onRefresh={() => {
          void reepriseDetails.refetch();
        }}
        refreshLabel={
          imageImportMetadata ? translate("image_import_failed_retry") : undefined
        }
        supportEmail={supportEmail}
      />
    );
  }

  return (
    <>
      <div
        ref={topRef}
        className=" bg-neutral-50 px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-left md:h-full md:pb-0 place-items-center"
      >
        <div className="md:grid md:grid-cols-12 gap-11 md:h-full md:mr-10">
          <div className={"relative md:p-4 rounded-lg md:h-full md:col-span-6 md:col-start-3 mb-9"}>
            <ReadingAudioIntegration
              audioSrc={audioSrc}
              chapterNumber={chapter}
              generatedTextId={Number(generatedTextId)}
              isPodcastText={isPodcastText}
              isSpeechPending={createSpeechMutation.isPending}
              podcastTranscript={podcastTranscript}
              requestSpeech={() => {
                createSpeechMutation.mutate({
                  id: Number(generatedTextId),
                  chapter_number: chapter,
                });
              }}
            >
              {({ audioTrigger, playbackStateLayer }) => (
                <div className="relative rounded-2xl">
                  <GeneratedTextDetailsTextPane
                    generatedTextId={Number(generatedTextId)}
                    chapterNumber={chapter}
                    onVocabularyCreate={onVocabularyCreate}
                    highlightWords={activeHighlightWords}
                    focusRequest={focusRequest}
                    metadataAccessory={audioTrigger}
                    metadataSecondary={
                      <>
                        {imageImportStatusCard}
                        {sourceMetadataCard}
                        {playbackStateLayer}
                      </>
                    }
                    isInlineContinuationStreaming={inlineContinuationMutation.isPending}
                  />
                  {/* Experimental alternative to classic chapter creation: continue in-place. */}
                  <CreateVocabularyTextInlinePreview
                    isVisible={showInlineContinuationPreview}
                    isLoading={inlineContinuationMutation.isPending}
                    errorMessage={inlineContinuationErrorMessage}
                    onContinue={() => {
                      void continueTextInlineFromSelection();
                    }}
                  />
                </div>
              )}
            </ReadingAudioIntegration>
          </div>
          <div
            className={
              "bg-white-300 rounded-lg md:col-start-9 md:col-span-4 md:h-full "
            }
          >
            <h2 className="text-l bg-neutral-50 md:pt-5 font-semibold md:mb-1 mb-4 md:sticky top-0 z-10">
              {translate("vocabulary")}
            </h2>
            {vocabularies.length === 0 && (
              <div className="flex bg-slate-100 mb-4 p-3 rounded-md mt-4">
                <p>
                  <LightBulbIcon className="w-6 h-6 mr-1" />
                </p>
                {translate("translations_click_on_a_word_to_translate_it")}
              </div>
            )}
            {reepriseDetails.isSuccess && (
              <div className="mb-3 mt-1 py-2">
                <h3 className="mb-2 text-[11px] uppercase tracking-[0.14em] text-slate-700">
                  {translate("generated_text_grammar_lesson_title")}
                </h3>
                <button
                  data-testid="grammar-lesson-generate-button"
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700 hover:bg-slate-50 cursor-pointer"
                  onClick={() => {
                    void generateGrammarLesson();
                  }}
                  disabled={isGrammarLessonLoading || reepriseDetails.data?.status !== "COMPLETE"}
                >
                  {isGrammarLessonLoading
                    ? translate("generated_text_grammar_lesson_generating")
                    : translate("generated_text_grammar_lesson_generate")}
                </button>
                {grammarLessonError && (
                  <p className="mt-2 text-xs text-rose-700">{grammarLessonError}</p>
                )}
                {grammarLesson && (
                  <div
                    data-testid="grammar-lesson-body"
                    className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
                  >
                    {grammarLesson.title && (
                      <p className="font-semibold text-slate-900">{grammarLesson.title}</p>
                    )}
                    {grammarLesson.summary && (
                      <p className="mt-1 whitespace-pre-line">{grammarLesson.summary}</p>
                    )}
                    {grammarLesson.points.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {grammarLesson.points.map((point, index) => (
                          <div key={`${point.pattern}-${index}`} className="rounded-md border border-slate-200 bg-white p-2">
                            {point.pattern && (
                              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                                {point.pattern}
                              </p>
                            )}
                            {point.example && (
                              <p className="mt-1 italic text-slate-700">{point.example}</p>
                            )}
                            {point.explanation && (
                              <p className="mt-1 text-slate-700">{point.explanation}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {reepriseDetails.isSuccess && (
              <div className="mb-3 mt-1 py-2">
                <button
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700 hover:bg-slate-50 cursor-pointer"
                  onClick={toggleTrainerForCurrentText}
                >
                  {translate(
                    showTextScopedTrainer
                      ? "trainer_hide_for_this_text"
                      : "trainer_start_for_this_text",
                  )}
                </button>
              </div>
            )}
            {showTextScopedTrainer && (
              <div className="mb-4">
                <VocabularyPreviewCard generatedTextId={Number(generatedTextId)} />
              </div>
            )}
            {reepriseDetails.isSuccess &&
              chapter === undefined &&
              !isImportedReadingText && (
              <div className="mb-3 mt-1 hidden py-2 md:block">
                <button
                  data-testid="create-vocabulary-text-classic-button-desktop"
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-slate-700 hover:bg-slate-50 cursor-pointer"
                  onClick={() => {
                    void continueTextInlineFromSelection();
                  }}
                >
                  {translate(chapterContinueButtonLabelKey)}
                </button>
              </div>
            )}
            <ul className="p-0 m-0 w-full z-20 md:pt-1 pb-24 md:pb-0">
              {vocabularies.map((arg) => (
                <VocabularyItem
                  key={arg.id}
                  vocabulary={arg}
                  isSelected={selectedVocabularyIds.includes(arg.id)}
                  addOrRemoveVocabulary={() => {
                    if (selectedVocabularyIds.includes(arg.id)) {
                      setSelectedVocabularyIds((prev) =>
                        prev.filter((entry) => entry !== arg.id),
                      );
                      setDeselectedVocabularyIds((prev) => [
                        ...prev.filter((entry) => entry !== arg.id),
                        arg.id,
                      ]);
                    } else {
                      setSelectedVocabularyIds((prev) =>
                        prev.includes(arg.id) ? prev : [...prev, arg.id],
                      );
                      setDeselectedVocabularyIds((prev) =>
                        prev.filter((entry) => entry !== arg.id),
                      );
                    }
                  }}
                  onJump={() => {
                    setFocusRequest({
                      word: arg.word,
                      sentence: arg.sentence ?? null,
                      token: Date.now(),
                    });
                  }}
                  textLevel={reepriseDetails.data?.level}
                ></VocabularyItem>
              ))}
              {paginatedVocabularyList.hasNextPage && (
                <li className="bg-blue-500 p-3 rounded-lg shadow-lg mb-2 w-full">
                  <button
                    className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200"
                    onClick={() => {
                      paginatedVocabularyList.fetchNextPage();
                    }}
                  >
                    Load more
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      {isLanguageHelperOpen && (
        <div
          data-testid="language-helper-overlay"
          className="fixed inset-0 z-50 flex items-start justify-end bg-slate-950/20 px-4 pt-20 pb-4 md:px-8"
          onClick={() => setIsLanguageHelperOpen(false)}
        >
          <div
            className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-3"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                data-testid="language-helper-overlay-close"
                aria-label={translate("text_card_close_prompt")}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                onClick={() => setIsLanguageHelperOpen(false)}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <LanguageHelperPanel
              generatedTextId={Number(generatedTextId)}
              chapterNumber={chapter}
              wordLanguage={
                languageSpace !== "ALL"
                  ? languageSpace
                  : reepriseDetails.data?.language
              }
              contextText={
                chapter !== undefined
                  ? chapterDetails.data &&
                    "content" in chapterDetails.data &&
                    chapterDetails.data.status === "COMPLETE"
                    ? chapterDetails.data.content
                    : undefined
                  : reepriseDetails.data &&
                      "chatgpt_output" in reepriseDetails.data
                    ? reepriseDetails.data.chatgpt_output
                    : undefined
              }
              recentVocabulary={recentVocabulary}
            />
          </div>
        </div>
      )}
    </>
  );
}

function ReadingAudioIntegration(props: {
  audioSrc: string;
  chapterNumber: number | undefined;
  generatedTextId: number;
  isPodcastText: boolean;
  isSpeechPending: boolean;
  podcastTranscript: string;
  requestSpeech: () => void;
  children: (slots: {
    audioTrigger: ReactNode;
    playbackStateLayer: ReactNode;
  }) => ReactNode;
}) {
  const { translate } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioProgressRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isAudioScrubbing, setIsAudioScrubbing] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioPlaybackRate, setAudioPlaybackRate] = useState(1);
  const [hasStartedPlayback, setHasStartedPlayback] = useState(false);
  const [isPlaybackStateLayerOpen, setIsPlaybackStateLayerOpen] = useState(false);
  const [showPodcastTranscript, setShowPodcastTranscript] = useState(false);
  const showAudioLoading = isAudioLoading || props.isSpeechPending;

  useEffect(() => {
    const audioEl = audioRef.current;
    if (audioEl) {
      audioEl.pause();
      audioEl.currentTime = 0;
    }
    setIsPlaying(false);
    setIsAudioLoading(false);
    setIsAudioScrubbing(false);
    setAudioCurrentTime(0);
    setAudioDuration(0);
    setAudioPlaybackRate(1);
    setHasStartedPlayback(false);
    setIsPlaybackStateLayerOpen(false);
    setShowPodcastTranscript(false);
  }, [props.audioSrc, props.chapterNumber, props.generatedTextId]);

  const seekAudioByClientX = (clientX: number) => {
    const progressEl = audioProgressRef.current;
    const audioEl = audioRef.current;
    if (!progressEl || !audioEl) {
      return;
    }
    const duration = Number.isFinite(audioEl.duration) ? audioEl.duration : 0;
    if (duration <= 0) {
      return;
    }
    const bounds = progressEl.getBoundingClientRect();
    if (bounds.width <= 0) {
      return;
    }
    const ratio = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
    const nextTime = ratio * duration;
    audioEl.currentTime = nextTime;
    setAudioCurrentTime(nextTime);
  };

  useEffect(() => {
    if (!isAudioScrubbing) {
      return;
    }
    const onMouseMove = (event: MouseEvent) => {
      seekAudioByClientX(event.clientX);
    };
    const onMouseUp = () => {
      setIsAudioScrubbing(false);
    };
    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      seekAudioByClientX(touch.clientX);
    };
    const onTouchEnd = () => {
      setIsAudioScrubbing(false);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isAudioScrubbing]);

  const togglePlayback = () => {
    const audioEl = audioRef.current;
    if (!audioEl) {
      return;
    }
    setIsPlaybackStateLayerOpen(true);
    if (isPlaying) {
      audioEl.pause();
      return;
    }
    props.requestSpeech();
    setIsAudioLoading(true);
    void audioEl.play().catch((error) => {
      console.error("Audio play error", error);
      setIsAudioLoading(false);
    });
  };

  const cyclePlaybackRate = () => {
    const nextRate = resolveNextPlaybackRate(audioPlaybackRate);
    const audioEl = audioRef.current;
    if (audioEl) {
      audioEl.playbackRate = nextRate;
    }
    setAudioPlaybackRate(nextRate);
    setIsPlaybackStateLayerOpen(true);
  };

  const seekByDeltaSeconds = (deltaSeconds: number) => {
    const audioEl = audioRef.current;
    if (!audioEl) {
      return;
    }
    const duration = Number.isFinite(audioEl.duration)
      ? audioEl.duration
      : audioDuration;
    const nextTime = resolveSeekTime({
      currentTime: audioEl.currentTime,
      deltaSeconds,
      duration,
    });
    audioEl.currentTime = nextTime;
    setAudioCurrentTime(nextTime);
    setHasStartedPlayback(true);
    setIsPlaybackStateLayerOpen(true);
  };

  const progressPercentage =
    audioDuration > 0
      ? Math.min(100, (audioCurrentTime / audioDuration) * 100)
      : 0;
  const shouldShowPlaybackStateLayer =
    hasStartedPlayback && (isPlaying || isPlaybackStateLayerOpen || isAudioScrubbing);
  const hasPlayableDuration = audioDuration > 0;

  return (
    <>
      {props.children({
        audioTrigger: (
          <AudioTrigger
            duration={hasPlayableDuration ? formatAudioClock(audioDuration) : null}
            isLoading={showAudioLoading}
            isPlaying={isPlaying}
            label={translate("audio_player_title")}
            onActivateLayer={() => {
              if (hasStartedPlayback) {
                setIsPlaybackStateLayerOpen(true);
              }
            }}
            onTogglePlayback={togglePlayback}
          />
        ),
        playbackStateLayer: (
          <PlaybackStateLayer
            isVisible={shouldShowPlaybackStateLayer}
            onBlurCapture={(event) => {
              const nextTarget = event.relatedTarget as Node | null;
              if (
                nextTarget &&
                event.currentTarget.contains(nextTarget)
              ) {
                return;
              }
              if (!isPlaying && !isAudioScrubbing) {
                setIsPlaybackStateLayerOpen(false);
              }
            }}
            onFocusCapture={() => {
              if (hasStartedPlayback) {
                setIsPlaybackStateLayerOpen(true);
              }
            }}
            onMouseEnter={() => {
              if (hasStartedPlayback) {
                setIsPlaybackStateLayerOpen(true);
              }
            }}
            onMouseLeave={() => {
              if (!isPlaying && !isAudioScrubbing) {
                setIsPlaybackStateLayerOpen(false);
              }
            }}
          >
            <PlaybackControls
              audioCurrentTime={audioCurrentTime}
              audioDuration={audioDuration}
              audioPlaybackRate={audioPlaybackRate}
              isPlaying={isPlaying}
              onCyclePlaybackRate={cyclePlaybackRate}
              onSeekBackward={() => seekByDeltaSeconds(-10)}
              onSeekForward={() => seekByDeltaSeconds(10)}
              onStartScrub={(clientX) => {
                seekAudioByClientX(clientX);
                setIsAudioScrubbing(true);
                setIsPlaybackStateLayerOpen(true);
              }}
              onTogglePlayback={togglePlayback}
              progressPercentage={progressPercentage}
              progressRef={audioProgressRef}
              translate={translate}
            />
            {props.isPodcastText && (
              <div className="mt-2">
                <button
                  type="button"
                  className="text-xs text-slate-600 underline underline-offset-2 hover:text-slate-800"
                  onClick={() => {
                    setShowPodcastTranscript((prev) => !prev);
                    setIsPlaybackStateLayerOpen(true);
                  }}
                >
                  {showPodcastTranscript
                    ? translate("podcast_details_hide_transcript")
                    : translate("podcast_details_show_transcript")}
                </button>
                {showPodcastTranscript && (
                  <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                    <p className="text-sm font-ABee-zee leading-tight whitespace-pre-line text-slate-700">
                      {props.podcastTranscript || translate("podcast_details_transcript_pending")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </PlaybackStateLayer>
        ),
      })}
      <audio
        key={props.audioSrc}
        ref={audioRef}
        src={props.audioSrc}
        preload="none"
        playsInline
        className="hidden"
        onPlaying={() => {
          setIsPlaying(true);
          setIsAudioLoading(false);
          setHasStartedPlayback(true);
          setIsPlaybackStateLayerOpen(true);
        }}
        onPause={() => {
          setIsPlaying(false);
        }}
        onWaiting={() => setIsAudioLoading(true)}
        onStalled={() => setIsAudioLoading(true)}
        onError={() => setIsAudioLoading(false)}
        onLoadedMetadata={(event) => {
          setAudioDuration(event.currentTarget.duration || 0);
          setAudioPlaybackRate(event.currentTarget.playbackRate || 1);
        }}
        onTimeUpdate={(event) => {
          setAudioCurrentTime(event.currentTarget.currentTime || 0);
        }}
        onEnded={(event) => {
          setIsPlaying(false);
          setAudioCurrentTime(event.currentTarget.duration || 0);
          setIsPlaybackStateLayerOpen(false);
        }}
      />
    </>
  );
}

function AudioTrigger(props: {
  label: string;
  duration: string | null;
  isLoading: boolean;
  isPlaying: boolean;
  onTogglePlayback: () => void;
  onActivateLayer: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={props.onTogglePlayback}
        onMouseEnter={props.onActivateLayer}
        onFocus={props.onActivateLayer}
        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-900 active:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        aria-label={props.label}
      >
        {props.isLoading ? (
          <span
            aria-hidden="true"
            className="h-5 w-5 animate-spin rounded-full border-2 border-slate-400 border-t-transparent"
          />
        ) : props.isPlaying ? (
          <PauseIcon className="h-5 w-5" />
        ) : (
          <PlayIcon className="h-5 w-5" />
        )}
      </button>
      <span className="text-sm text-slate-700">{props.label}</span>
    </span>
  );
}

function PlaybackStateLayer(props: {
  isVisible: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocusCapture: () => void;
  onBlurCapture: (event: FocusEvent<HTMLDivElement>) => void;
  children: ReactNode;
}) {
  return (
    <div
      className={[
        "overflow-hidden transition-[max-height,opacity,transform] duration-200",
        props.isVisible
          ? "max-h-72 opacity-100 translate-y-0"
          : "pointer-events-none max-h-0 -translate-y-1 opacity-0",
      ].join(" ")}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      onFocusCapture={props.onFocusCapture}
      onBlurCapture={props.onBlurCapture}
    >
      <div className="pt-2">{props.children}</div>
    </div>
  );
}

function PlaybackControls(props: {
  audioCurrentTime: number;
  audioDuration: number;
  audioPlaybackRate: number;
  isPlaying: boolean;
  progressPercentage: number;
  progressRef: RefObject<HTMLDivElement | null>;
  onTogglePlayback: () => void;
  onSeekBackward: () => void;
  onSeekForward: () => void;
  onCyclePlaybackRate: () => void;
  onStartScrub: (clientX: number) => void;
  translate: ReturnType<typeof useTranslation>["translate"];
}) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
      <button
        type="button"
        onClick={props.onTogglePlayback}
        className="rounded-full px-1 py-0.5 text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        aria-label={
          props.isPlaying
            ? props.translate("audio_player_pause")
            : props.translate("audio_player_play")
        }
      >
        {props.isPlaying ? "❚❚" : "▶"}
      </button>
      <span className="w-9 text-right tabular-nums text-slate-500">
        {formatAudioClock(props.audioCurrentTime)}
      </span>
      <div
        ref={props.progressRef}
        className="relative h-4 flex-1 cursor-pointer touch-none"
        onMouseDown={(event) => {
          props.onStartScrub(event.clientX);
        }}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          if (!touch) {
            return;
          }
          props.onStartScrub(touch.clientX);
        }}
        role="slider"
        aria-label={props.translate("audio_player_title")}
        aria-valuemin={0}
        aria-valuemax={props.audioDuration || 0}
        aria-valuenow={props.audioCurrentTime}
      >
        <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-slate-200" />
        <div
          className="absolute left-0 top-1/2 h-px -translate-y-1/2 bg-slate-500 transition-[width] duration-150"
          style={{ width: `${props.progressPercentage}%` }}
        />
        <button
          type="button"
          className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-slate-400 bg-white"
          style={{ left: `${props.progressPercentage}%` }}
          onMouseDown={(event) => {
            event.stopPropagation();
            props.onStartScrub(event.clientX);
          }}
          onTouchStart={(event) => {
            event.stopPropagation();
            const touch = event.touches[0];
            if (!touch) {
              return;
            }
            props.onStartScrub(touch.clientX);
          }}
          aria-label={props.translate("audio_player_title")}
        />
      </div>
      <span className="w-9 tabular-nums text-slate-500">
        {formatAudioClock(props.audioDuration)}
      </span>
      <button
        type="button"
        onClick={props.onSeekBackward}
        className="rounded px-1 py-0.5 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        aria-label={props.translate("audio_player_skip_back")}
      >
        ↺10
      </button>
      <button
        type="button"
        onClick={props.onCyclePlaybackRate}
        className="rounded px-1 py-0.5 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        aria-label={props.translate("audio_player_speed")}
      >
        {`${props.audioPlaybackRate}x`}
      </button>
      <button
        type="button"
        onClick={props.onSeekForward}
        className="rounded px-1 py-0.5 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        aria-label={props.translate("audio_player_skip_forward")}
      >
        10↻
      </button>
    </div>
  );
}

function FailedGeneratedTextScreen(props: {
  generatedTextId: number;
  supportEmail: string;
  contactHref: string;
  onRefresh: () => void;
  customBody?: string;
  refreshLabel?: string;
}) {
  const { translate } = useTranslation();

  return (
    <div className="min-h-full bg-[radial-gradient(circle_at_top,rgba(186,230,253,0.55),transparent_38%),linear-gradient(180deg,#f8fafc_0%,#eef6ff_100%)] px-5 py-8 md:px-8">
      <div className="mx-auto max-w-3xl rounded-[32px] border border-sky-200/80 bg-white/90 p-6 md:p-10">
        <div className="mx-auto flex max-w-xl flex-col items-center text-center">
          <FailureIllustration />
          <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-sky-700">
            {translate("generated_text_failed_badge")}
          </p>
          <h1 className="mt-3 font-serif text-3xl text-slate-900 md:text-4xl">
            {translate("generated_text_failed_title")}
          </h1>
          <p className="mt-4 max-w-lg text-base leading-7 text-slate-700">
            {props.customBody || translate("generated_text_failed_body")}
          </p>
          <p className="mt-3 text-sm text-slate-500">
            {translate("generated_text_failed_contact_note", {
              email: props.supportEmail,
            })}
          </p>
          <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
            <a
              href={props.contactHref}
              className="inline-flex min-h-12 items-center justify-center rounded-full border border-sky-300 bg-sky-100 px-6 text-base font-semibold text-sky-900 transition-colors hover:bg-sky-200"
            >
              {translate("generated_text_failed_contact")}
            </a>
            <button
              type="button"
              onClick={props.onRefresh}
              className="inline-flex min-h-12 items-center justify-center rounded-full border border-slate-300 bg-white px-6 text-base font-semibold text-slate-800 transition-colors hover:bg-slate-50"
            >
              {props.refreshLabel || translate("generated_text_failed_refresh")}
            </button>
          </div>
          <Link
            to="/app/reeprise"
            search={{ mobileView: undefined }}
            className="mt-4 inline-flex min-h-12 items-center justify-center rounded-full px-5 text-base font-semibold text-slate-600 underline underline-offset-4"
          >
            {translate("generated_text_failed_back")}
          </Link>
          <p className="mt-8 text-xs uppercase tracking-[0.18em] text-slate-400">
            {`#${props.generatedTextId}`}
          </p>
        </div>
      </div>
    </div>
  );
}

function ImageImportStatusCard(props: {
  metadata: ImageImportMetadata;
  status: "PENDING" | "COMPLETE" | "FAILED" | undefined;
}) {
  const { translate } = useTranslation();

  if (props.status === "PENDING") {
    return (
      <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <p className="font-semibold text-slate-900">
          {translate(
            resolveImageImportStageLabelKey(
              props.metadata.processing_stage,
              props.metadata.mode,
            ),
          )}
        </p>
        <p className="mt-1 text-slate-600">
          {translate("image_import_processing_notice")}
        </p>
      </div>
    );
  }

  if (props.status === "COMPLETE" && props.metadata.shortened) {
    return (
      <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <p className="font-semibold text-slate-900">
          {translate("image_import_shortened_title")}
        </p>
        <p className="mt-1 text-slate-600">
          {props.metadata.shortening_reason?.trim() ||
            translate("image_import_shortened_body")}
        </p>
      </div>
    );
  }

  return null;
}

function ArticleImportSourceCard(props: { metadata: ImageImportMetadata }) {
  const { translate } = useTranslation();
  const sourceUrl = props.metadata.canonical_url || props.metadata.source_url;
  const sourceBits = [
    props.metadata.site_name?.trim(),
    props.metadata.source_language?.trim(),
  ].filter(Boolean);

  if (
    !sourceUrl &&
    !props.metadata.original_title?.trim() &&
    !props.metadata.excerpt?.trim() &&
    sourceBits.length === 0
  ) {
    return null;
  }

  return (
    <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
      <p className="font-semibold text-slate-900">
        {translate("article_import_source_title")}
      </p>
      {props.metadata.original_title?.trim() && (
        <p className="mt-1 text-slate-800">{props.metadata.original_title.trim()}</p>
      )}
      {sourceBits.length > 0 && (
        <p className="mt-1 text-slate-600">{sourceBits.join(" · ")}</p>
      )}
      {props.metadata.excerpt?.trim() && (
        <p className="mt-2 text-slate-600">{props.metadata.excerpt.trim()}</p>
      )}
      {sourceUrl && (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-2 block break-all text-slate-700 underline"
        >
          {sourceUrl}
        </a>
      )}
    </div>
  );
}

function FailureIllustration() {
  return (
    <div
      aria-hidden="true"
      className="relative h-56 w-full max-w-[20rem] overflow-hidden rounded-[28px] border border-sky-200 bg-[linear-gradient(180deg,#dff3ff_0%,#f7fbff_100%)]"
    >
      <div className="absolute inset-x-6 bottom-0 h-24 rounded-t-[24px] border-x border-t border-slate-300 bg-white" />
      <div className="absolute left-1/2 top-[3.65rem] h-16 w-12 -translate-x-1/2 rounded-[16px] border border-slate-300 bg-slate-50" />
      <div className="absolute left-1/2 top-[4.55rem] h-1 w-8 -translate-x-1/2 rounded-full bg-slate-300" />
      <div className="absolute left-1/2 top-[5.2rem] h-1 w-6 -translate-x-1/2 rounded-full bg-slate-200" />
      <div className="absolute left-1/2 top-[1.25rem] h-20 w-20 -translate-x-1/2 rounded-full border border-amber-300 bg-[radial-gradient(circle,#fde68a_0%,#fcd34d_72%,#fbbf24_100%)]" />
      <div className="absolute left-[2.1rem] top-[6.2rem] h-12 w-20 rounded-full border border-white/80 bg-white/80" />
      <div className="absolute left-[3.4rem] top-[5.5rem] h-14 w-14 rounded-full border border-white/80 bg-white/90" />
      <div className="absolute right-[2rem] top-[5.7rem] h-12 w-20 rounded-full border border-white/80 bg-white/80" />
      <div className="absolute right-[3.2rem] top-[5.05rem] h-14 w-14 rounded-full border border-white/80 bg-white/90" />
      <div className="absolute bottom-[5.35rem] left-1/2 h-10 w-10 -translate-x-1/2 rounded-full border border-rose-300 bg-rose-100" />
      <div className="absolute bottom-[6.1rem] left-[7.6rem] h-4 w-4 rounded-full bg-white/80" />
      <div className="absolute bottom-[6.4rem] right-[7.5rem] h-4 w-4 rounded-full bg-white/80" />
      <div className="absolute bottom-[4.9rem] left-1/2 h-0.5 w-16 -translate-x-1/2 bg-rose-300" />
    </div>
  );
}

function formatAudioClock(rawSeconds: number) {
  if (!Number.isFinite(rawSeconds) || rawSeconds < 0) {
    return "0:00";
  }
  const totalSeconds = Math.floor(rawSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function VocabularyItem(props: {
  vocabulary: VocabularyDto;
  isSelected: boolean;
  addOrRemoveVocabulary: () => void;
  onJump: () => void;
  textLevel?: string;
}) {
  const [toggled, setToggled] = useState(false);
  const [showMoreStructure, setShowMoreStructure] = useState(true);
  const [translationRevealStage, setTranslationRevealStage] = useState(0);
  const { translate } = useTranslation();
  const translationDetails = normalizeVocabularyTranslationDetails({
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const structureDetails = getVocabularyStructureDetails({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const thinkeraCoreIdea =
    props.textLevel === THINKERA_LEVEL && structureDetails.core_idea
      ? structureDetails.core_idea
      : null;
  const primaryTranslation =
    thinkeraCoreIdea ??
    getVocabularyPrimaryTranslation({
      translation: props.vocabulary.translation,
      translation_details: props.vocabulary.translation_details,
    });
  const primaryWord = getVocabularyPrimaryWord({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const surfaceWord = getVocabularySurfaceWord({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const baseWord = getVocabularyBaseWord({
    word: props.vocabulary.word,
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const sourceSentence = props.vocabulary.sentence?.trim() || null;
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence =
    translatedSentence && translatedSentence !== primaryTranslation
      ? translatedSentence
      : null;
  const hasStructureDetails = Boolean(
    structureDetails.core_idea ||
      structureDetails.word_meaning ||
      structureDetails.form_note ||
      structureDetails.pattern_explanation ||
      structureDetails.contextual_meaning ||
      structureDetails.example_breakdown.length > 0 ||
      structureDetails.related_examples.length > 0,
  );
  const translationsRevealed =
    translationRevealStage >= VOCABULARY_REVEAL_STAGE_COUNT - 1;

  return (
    <li
      className={
        "group relative mb-4 rounded-xl border border-slate-200 bg-white p-3 sm:p-4 " +
        (toggled ? "" : "cursor-pointer")
      }
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest("[data-vocabulary-control='true']")) {
          return;
        }
        if (!toggled) {
          props.onJump();
        }
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p className="m-0 px-1 font-serif text-xl font-semibold leading-snug text-slate-900">
            {primaryWord}
          </p>
          <div data-vocabulary-control="true">
            <VocabularyPronunciationButton vocabulary={props.vocabulary} />
          </div>
        </div>
        <input
          type="checkbox"
          data-vocabulary-control="true"
          className="appearance-none h-5 w-5 rounded-[6px] border border-slate-300 bg-white transition-colors duration-150 cursor-pointer relative
            hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2
            checked:bg-slate-900 checked:border-slate-900
            checked:after:content-[''] checked:after:absolute checked:after:w-[6px] checked:after:h-[10px] checked:after:border-r-[2px] checked:after:border-b-[2px] checked:after:border-white checked:after:rotate-45 checked:after:left-1/2 checked:after:top-1/2 checked:after:-translate-x-1/2 checked:after:-translate-y-[55%]"
          checked={props.isSelected}
          onChange={props.addOrRemoveVocabulary}
          onClick={(event) => event.stopPropagation()}
        />
      </div>
      <p className="mb-1 mt-2 px-1 text-xs text-slate-500">
        {translate("vocabulary_guess_prompt")}
      </p>
      <button
        type="button"
        data-vocabulary-control="true"
        onClick={(event) => {
          event.stopPropagation();
          setTranslationRevealStage((prev) =>
            advanceVocabularyRevealStage(prev),
          );
        }}
        className="mb-0 mt-0 block w-full bg-transparent px-1 text-left"
        aria-label={
          translationsRevealed
            ? translate("vocabulary_guess_translation_visible")
            : translate("vocabulary_guess_translation_hidden")
        }
      >
        <VocabularyRevealText
          text={primaryTranslation}
          revealLevel={translationRevealStage}
          revealed={translationsRevealed}
          className="font-serif text-lg font-normal leading-snug transition"
          hiddenClassName="select-none text-slate-500"
          visibleClassName="text-slate-600"
        />
      </button>
      <p className="mb-2 mt-1 px-1 text-[11px] uppercase tracking-wide text-slate-400">
        {translate(
          translationsRevealed
            ? "vocabulary_guess_tap_to_hide_hint"
            : "vocabulary_guess_tap_to_reveal_more_hint",
        )}
      </p>
      {surfaceWord !== primaryWord && (
        <p className="mb-2 px-1 text-xs text-slate-600">
          <span className="font-semibold">
            {translate("vocabulary_label_seen_form")}:
          </span>{" "}
          {surfaceWord}
        </p>
      )}
      <div className="mt-3 pt-2 space-y-1">
        <p className="m-0 text-left font-serif text-sm font-normal leading-snug text-slate-700">
          {primarySentence
            ? highlightVocabularyWordInSentence(primarySentence, surfaceWord)
            : translate("vocabulary_source_sentence_unavailable")}
        </p>
        {secondarySentence && (
          <button
            type="button"
            data-vocabulary-control="true"
            onClick={(event) => {
              event.stopPropagation();
              setTranslationRevealStage((prev) =>
                advanceVocabularyRevealStage(prev),
              );
            }}
            className="m-0 block w-full bg-transparent p-0 text-left"
            aria-label={
              translationsRevealed
                ? translate("vocabulary_guess_translation_visible")
                : translate("vocabulary_guess_translation_hidden")
            }
          >
            <VocabularyRevealText
              text={secondarySentence}
              revealLevel={translationRevealStage}
              revealed={translationsRevealed}
              className="font-serif text-sm font-normal leading-snug transition"
              hiddenClassName="select-none text-slate-400"
              visibleClassName="text-slate-500"
            />
          </button>
        )}
      </div>
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/40 p-3 space-y-2">
        {baseWord && (
          <p className="text-xs text-slate-600">
            <span className="font-semibold">
              {translate("vocabulary_label_base_form")}:
            </span>{" "}
            {baseWord}
          </p>
        )}
        {hasStructureDetails && (
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">
              {translate("vocabulary_label_structure")}
            </p>
            <button
              type="button"
              data-vocabulary-control="true"
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                setShowMoreStructure((prev) => !prev);
              }}
              className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50 focus-visible:outline-none"
            >
              {showMoreStructure
                ? translate("vocabulary_less_structure")
                : translate("vocabulary_more_structure")}
            </button>
          </div>
        )}
        {showMoreStructure && hasStructureDetails && (
          <div className="space-y-2 text-xs text-slate-700">
            {structureDetails.core_idea && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_core_idea")}:
                </span>{" "}
                {structureDetails.core_idea}
              </p>
            )}
            {structureDetails.word_meaning && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_word")}:
                </span>{" "}
                {structureDetails.word_meaning}
              </p>
            )}
            {structureDetails.form_note && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_form_note")}:
                </span>{" "}
                {structureDetails.form_note}
              </p>
            )}
            {structureDetails.pattern_explanation && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_pattern")}:
                </span>{" "}
                {structureDetails.pattern_explanation}
              </p>
            )}
            {structureDetails.contextual_meaning && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_in_context")}:
                </span>{" "}
                {structureDetails.contextual_meaning}
              </p>
            )}
            {structureDetails.example_breakdown.length > 0 && (
              <div>
                <p className="font-semibold">
                  {translate("vocabulary_label_breakdown")}:
                </p>
                <ul className="list-disc pl-4">
                  {structureDetails.example_breakdown.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {structureDetails.related_examples.length > 0 && (
              <div>
                <p className="font-semibold">
                  {translate("vocabulary_label_related_examples")}:
                </p>
                <ul className="list-disc pl-4">
                  {structureDetails.related_examples.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      

      {toggled ? (
        <MnemonicEditForm vocabulary={props.vocabulary} />
      ) : (
        <p className="mt-2 px-1 font-serif text-base font-normal leading-snug text-slate-700">
          {props.vocabulary.mnemonic}
        </p>
      )}
      <div className="flex justify-between my-2">
        <button
          data-vocabulary-control="true"
          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-base hover:bg-slate-50"
          onClick={(event) => {
            event.stopPropagation();
            setToggled(!toggled);
          }}
        >
          {toggled ? "❌" : "🖊️"}
        </button>
      </div>
    </li>
  );
}

type MnemonicFormValues = {
  mnemonic: string;
};

function MnemonicEditForm(props: { vocabulary: VocabularyDto }) {
  const updateVocabulary = useVocabularyUpdate();
  const { register, handleSubmit } = useForm<MnemonicFormValues>();
  const onSubmit: SubmitHandler<MnemonicFormValues> = (data) => {
    void updateVocabulary.mutateAsync({
      id: props.vocabulary.id,
      patchVocabularyDto: {
        mnemonic: data.mnemonic,
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex justify-between items-center"
    >
      <textarea
        {...register("mnemonic")}
        rows={5}
        cols={40}
        className="grow p-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
        defaultValue={props.vocabulary.mnemonic ?? ""}
      ></textarea>
      <input
        type="submit"
        value="☑️"
        className="m-2 text-2xl border-gray-300"
      />
    </form>
  );
}
